import type { PlacementFormPosition } from "@design-lab/shared/types";

import {
  getPrintStandardPlacement,
  type GarmentCategory,
  type StandardPlacementDimensions
} from "./printStandardPlacement";

/**
 * MN-8890 wiring — garment-size-string → standard placement resolver.
 *
 * The foundation table (printStandardPlacement.ts) is keyed by (placement, category, sizeRange)
 * where the sizeRange buckets are PLACEMENT-SPECIFIC (e.g. adult "XL" is its own bucket on `front`
 * but rolls into "L+" on `back`/`frontLeftChest`). This module turns a raw Spree variant size
 * string (e.g. "M", "2XL", "YL", "3T", "12M") into the right bucket for a given placement and
 * returns the standard dimensions.
 *
 * Fail-safe contract (Kevin B1b, comment 246810): any size string that does NOT map cleanly to a
 * standard size class returns `null` — the caller must then leave artwork on legacy/manual sizing
 * and flag for CST. NEVER guess a placement.
 *
 * !!! DECORATION REVIEW REQUIRED (Joe B1b, comment 246918) !!!
 * The size→class boundaries below are written out EXPLICITLY (not inferred) so decoration can
 * red-line them. The judgment calls to confirm:
 *   - Where plain "XL" sits: adult XL → "XL" on front, but "L+" on back/left-chest.
 *   - 2XL+ all collapse to the largest bucket ("XXL+" front, "L+" back/left-chest).
 *   - Combo sizes ("XS/S", "L/XL"), tall ("XL TALL"), and OSFA/one-size deliberately return null
 *     (ambiguous → manual + flag CST) rather than being force-bucketed.
 *   - youth back uses "XS - S" / "M+"; youth left-chest uses "XS - M" / "L+" (asymmetric on purpose,
 *     mirrors the Transfers source table).
 *   - Boundary sizes that currently fail safe to null (decoration to confirm whether they should
 *     map): "1X" / "1XL" (vendor synonym for XL — not auto-mapped); loose infant months below 6M
 *     such as "3M" / "0-3M" (classified infant but not in a bucket → null; decoration decides if
 *     sub-6M prints share the "6M - 24M" standard).
 */

export type GarmentClassification = {
  category: GarmentCategory;
  /** Canonical size token within the category (e.g. adult "M", youth "L", toddler "4T", infant "12M"). */
  size: string;
};

const ADULT_SIZES = new Set(["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL"]);

// Raw variant strings → canonical {category, size}. Anything not listed returns null (fail-safe).
function classifyGarmentSize(rawSize: string): GarmentClassification | null {
  const s = rawSize.trim().toUpperCase();
  if (!s) return null;

  // Reject ambiguous/compound/tall/one-size up front — never guess these.
  if (s.includes("/") || s.includes("TALL") || s === "OSFA" || s === "ONE SIZE") return null;

  // Infant: Newborn + month sizes (NB, 0-3M ... 24M). The month match is intentionally broad;
  // months that aren't in an infant bucket (e.g. "3M") classify as infant but resolve to null at
  // the bucket step (fail-safe) rather than being force-fit into "6M - 24M".
  if (s === "NB" || s === "NEWBORN") return { category: "infant", size: "NB" };
  const monthMatch = s.match(/^(\d{1,2})\s*M$/); // "6M", "12M", "24M"
  if (monthMatch) return { category: "infant", size: `${monthMatch[1]}M` };

  // Toddler: 2T..6T.
  const toddlerMatch = s.match(/^([2-6])T$/);
  if (toddlerMatch) return { category: "toddler", size: `${toddlerMatch[1]}T` };

  // Youth: explicit "Y" prefix (YXS..YXL) or "YOUTH X". Both regexes keep the size in capture
  // group 1 by design — keep it that way if either is edited.
  const youthMatch = s.match(/^Y\s*(XS|S|M|L|XL)$/) || s.match(/^YOUTH\s+(XS|S|M|L|XL)$/);
  if (youthMatch?.[1]) return { category: "youth", size: youthMatch[1] };

  // Adult: plain XS..6XL, "2X".."6X" shorthand, and "XXL".."XXXXXXL" letter-repeat aliases.
  const adultShorthand = s.match(/^([2-6])X$/)?.[1]; // "2X" -> "2"
  const repeatLen = s.match(/^(XX+)L$/)?.[1]?.length; // "XXL" -> 2, "XXXL" -> 3
  let adult = s;
  if (adultShorthand) adult = `${adultShorthand}XL`;
  else if (repeatLen) adult = `${repeatLen}XL`;
  if (ADULT_SIZES.has(adult)) return { category: "adult", size: adult };

  return null;
}

// Explicit, decoration-reviewable bucket map: (placement, category) → ordered rules mapping a
// canonical size to the sizeRange label used in PRINT_STANDARD_PLACEMENT. `range: null` = the
// category-wide catch-all row (toddler/infant on back/left-chest). First matching rule wins.
type BucketRule = { sizes: string[]; range: string | null };

const ADULT_2XL_PLUS = ["2XL", "3XL", "4XL", "5XL", "6XL"];

const PLACEMENT_BUCKETS: Record<
  PlacementFormPosition,
  Partial<Record<GarmentCategory, BucketRule[]>>
> = {
  front: {
    adult: [
      { sizes: ["XS", "S"], range: "XS - S" },
      { sizes: ["M", "L"], range: "M - L" },
      { sizes: ["XL"], range: "XL" },
      { sizes: ADULT_2XL_PLUS, range: "XXL+" }
    ],
    youth: [
      { sizes: ["XS", "S"], range: "XS - S" },
      { sizes: ["M", "L"], range: "M - L" },
      { sizes: ["XL"], range: "XL" }
    ],
    toddler: [
      { sizes: ["2T", "3T", "4T"], range: "2T - 4T" },
      { sizes: ["5T", "6T"], range: "5T - 6T" }
    ],
    infant: [
      { sizes: ["NB"], range: "Newborn" },
      { sizes: ["6M", "12M", "18M", "24M"], range: "6M - 24M" }
    ]
  },
  frontLeftChest: {
    adult: [
      { sizes: ["XS", "S", "M"], range: "XS - M" },
      { sizes: ["L", "XL", ...ADULT_2XL_PLUS], range: "L+" }
    ],
    youth: [
      { sizes: ["XS", "S", "M"], range: "XS - M" },
      { sizes: ["L", "XL"], range: "L+" }
    ],
    toddler: [{ sizes: ["2T", "3T", "4T", "5T", "6T"], range: null }],
    infant: [{ sizes: ["NB", "6M", "12M", "18M", "24M"], range: null }]
  },
  back: {
    adult: [
      { sizes: ["XS", "S", "M"], range: "XS - M" },
      { sizes: ["L", "XL", ...ADULT_2XL_PLUS], range: "L+" }
    ],
    youth: [
      { sizes: ["XS", "S"], range: "XS - S" },
      { sizes: ["M", "L", "XL"], range: "M+" }
    ],
    toddler: [{ sizes: ["2T", "3T", "4T", "5T", "6T"], range: null }],
    infant: [{ sizes: ["NB", "6M", "12M", "18M", "24M"], range: null }]
  }
};

/**
 * Resolve the standard print placement dimensions for a raw garment size + placement.
 * Returns `null` (fail-safe) when the size can't be classified or its bucket isn't in the table —
 * callers must fall back to legacy/manual sizing and flag CST, never guess.
 */
export function getStandardPlacementForGarmentSize(
  placement: PlacementFormPosition,
  rawSize: string
): StandardPlacementDimensions | null {
  const classified = classifyGarmentSize(rawSize);
  if (!classified) return null;

  const rules = PLACEMENT_BUCKETS[placement]?.[classified.category];
  if (!rules) return null;

  const rule = rules.find(r => r.sizes.includes(classified.size));
  if (!rule) return null;

  // Omit sizeRange entirely for catch-all rows (exactOptionalPropertyTypes forbids an explicit
  // `sizeRange: undefined`).
  return getPrintStandardPlacement(
    rule.range
      ? { placement, category: classified.category, sizeRange: rule.range }
      : { placement, category: classified.category }
  );
}

export const __testing = { classifyGarmentSize };
